import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Film, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
  </svg>
);

export default function AuthPage({ mode }) {
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  const isLogin = mode === "login";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== null) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(name, email, password);
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-[#050505] text-white">
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-r border-white/10">
        <img src="https://images.unsplash.com/photo-1500462918059-b1a0cb512f1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxuZW9uJTIwZnV0dXJpc3RpYyUyMHR1bm5lbHxlbnwwfHx8fDE3ODcyODE4MjZ8MA&ixlib=rb-4.1.0&q=85"
          alt="neon" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="relative z-10 mt-auto p-12">
          <h2 className="font-display text-4xl font-semibold tracking-tight leading-none max-w-md">
            Your videos, reborn in <span className="text-[#00E5FF]">3D</span>.
          </h2>
          <p className="mt-4 text-white/60 font-light max-w-sm">Copyright-safe. Cinematic. Built to go viral.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-10" data-testid="auth-brand">
            <div className="w-8 h-8 rounded-md bg-[#00E5FF] flex items-center justify-center glow-cyan">
              <Film className="w-5 h-5 text-black" />
            </div>
            <span className="font-display text-lg font-semibold tracking-tight">VIRAL3D</span>
          </Link>
          <h1 className="font-display text-3xl font-semibold tracking-tight mb-2">
            {isLogin ? "Sign in" : "Create account"}
          </h1>
          <p className="text-white/50 font-light mb-8 text-sm">
            {isLogin ? "Welcome back to your studio." : "Start transforming videos in seconds."}
          </p>

          <Button type="button" onClick={signInWithGoogle} variant="outline"
            className="w-full h-11 rounded-full border-white/15 bg-white text-black hover:bg-white/90 font-medium"
            data-testid="google-signin-btn">
            <GoogleIcon />
            <span className="ml-2">Continue with Google</span>
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-white/30 text-xs uppercase tracking-wider">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label className="text-white/70 text-sm">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required
                  className="mt-1.5 bg-zinc-950 border-white/15 h-11 focus-visible:ring-[#00E5FF]"
                  placeholder="Your name" data-testid="auth-name-input" />
              </div>
            )}
            <div>
              <Label className="text-white/70 text-sm">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 bg-zinc-950 border-white/15 h-11 focus-visible:ring-[#00E5FF]"
                placeholder="you@example.com" data-testid="auth-email-input" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1.5 bg-zinc-950 border-white/15 h-11 focus-visible:ring-[#00E5FF]"
                placeholder="••••••••" data-testid="auth-password-input" />
              {isLogin && (
                <div className="mt-1.5 text-right">
                  <Link to="/forgot-password" className="text-xs text-white/50 hover:text-[#00E5FF]" data-testid="auth-forgot-link">
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>
            <Button type="submit" disabled={loading}
              className="w-full h-11 rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium"
              data-testid="auth-submit-btn">
              {loading ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
              {!loading && <ArrowRight className="w-4 h-4 ml-1" />}
            </Button>
          </form>

          <p className="mt-6 text-sm text-white/50">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-[#00E5FF] hover:underline"
              data-testid="auth-switch-link">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
